/**
 * Senpai Chatbot — renders the <senpai-chat> web component
 * ONLY when the user is authenticated.
 *
 * Uses OAuth2 delegation tokens so the chatbot can call the
 * beverages API on behalf of the user with scoped permissions.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { requestDelegation, revokeDelegation, hasDelegation } from '../lib/delegation.ts';

// ─── Configuration ────────────────────────────────────────────────────────────
// Set via env var at build time, or hardcode the client ID from the Developer Console.
const SENPAI_CLIENT_ID = import.meta.env.VITE_SENPAI_CLIENT_ID || '';
const SENPAI_API_URL = import.meta.env.VITE_SENPAI_API_URL || 'https://api-senpai.runninglime.com';
const SENPAI_SITE_KEY = import.meta.env.VITE_SENPAI_SITE_KEY || 'beverages-demo';
const SENPAI_WIDGET_URL =
    import.meta.env.VITE_SENPAI_WIDGET_URL ||
    'https://senpai-widget.pages.dev/senpai-chat.es.js';

// ─── Component ────────────────────────────────────────────────────────────────

export default function SenpaiChatbot() {
    const { user } = useAuth();
    const chatRef = useRef<HTMLElement | null>(null);
    const widgetLoaded = useRef(false);
    const delegationInFlight = useRef(false);

    // Load the widget ES module dynamically (only once)
    const ensureWidgetLoaded = useCallback(async () => {
        if (widgetLoaded.current) return;
        try {
            await import(/* @vite-ignore */ SENPAI_WIDGET_URL);
            widgetLoaded.current = true;
        } catch (err) {
            console.error('[SenpaiChatbot] Failed to load widget:', err);
        }
    }, []);

    // Request a delegation token and push it to the widget
    const refreshDelegation = useCallback(async () => {
        if (!SENPAI_CLIENT_ID || delegationInFlight.current) return;
        delegationInFlight.current = true;

        try {
            const token = await requestDelegation(SENPAI_CLIENT_ID);
            if (token && chatRef.current) {
                chatRef.current.setAttribute('user-token', token);
            }
        } catch (err) {
            console.warn('[SenpaiChatbot] Delegation failed:', err);
        } finally {
            delegationInFlight.current = false;
        }
    }, []);

    // Mount / unmount the widget based on auth state
    useEffect(() => {
        if (!user || !SENPAI_CLIENT_ID) {
            // Not authenticated → remove widget + revoke delegation
            if (chatRef.current) {
                chatRef.current.remove();
                chatRef.current = null;
            }
            revokeDelegation().catch(() => { });
            return;
        }

        // User is authenticated → load widget + set up delegation
        let cancelled = false;

        (async () => {
            await ensureWidgetLoaded();
            if (cancelled) return;

            // Get delegation token
            const token = await requestDelegation(SENPAI_CLIENT_ID);
            if (cancelled || !token) return;

            // Create the <senpai-chat> element if it doesn't exist yet
            if (!chatRef.current) {
                const el = document.createElement('senpai-chat');
                el.setAttribute('api-url', SENPAI_API_URL);
                el.setAttribute('site-key', SENPAI_SITE_KEY);
                el.setAttribute('user-token', token);
                el.setAttribute('position', 'bottom-right');

                // Refresh delegation token when user opens the chat
                el.addEventListener('senpai-open', () => {
                    if (!hasDelegation(SENPAI_CLIENT_ID)) {
                        refreshDelegation();
                    }
                });

                document.body.appendChild(el);
                chatRef.current = el;
            } else {
                chatRef.current.setAttribute('user-token', token);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user, ensureWidgetLoaded, refreshDelegation]);

    // Cleanup on full unmount
    useEffect(() => {
        return () => {
            if (chatRef.current) {
                chatRef.current.remove();
                chatRef.current = null;
            }
        };
    }, []);

    // This component renders nothing — the web component is appended to document.body
    return null;
}
